import { BiPieChartAlt2 } from 'react-icons/bi';
import { HiOutlineArrowsRightLeft } from 'react-icons/hi2';

import { Card, PieChart, Skeleton } from '@/components';
import { EmptyPage } from '@/components/EmptyPage';
import type { PieChartItem } from '@/components/PieChart/types';
import { formatCurrency } from '@/utils/formatCurrency.ts';
import ErrorPage from '@pages/Expenses/components/ErrorPage/ErrorPage.tsx';

import s from './CategorySpending.module.scss';

const SKELETON_ROWS = 5;

export type SpendingCategory = {
  title: string;
  sum: number;
  color: string;
  slug: string;
};

interface CategorySpendingProps {
  items: PieChartItem[];
  categories: SpendingCategory[];
  periodLabel: string;
  loading?: boolean;
  error?: string | null;
  title?: string;
  plain?: boolean;
  onSelectCategory: (slug: string) => void;
  onRetry?: () => void;
}

const CategorySpending = ({
  items,
  categories,
  periodLabel,
  loading,
  error,
  title = 'Spending by category',
  plain = false,
  onSelectCategory,
  onRetry,
}: CategorySpendingProps) => {
  const handleSliceClick = (title: string) => {
    const slug = categories.find((category) => category.title === title)?.slug;
    if (slug) onSelectCategory(slug);
  };

  const body = (
    <>
      {error && <ErrorPage error={error} onRetry={onRetry} className={s.error} />}
      {!error && loading && (
        <div className={s.layout} aria-busy="true">
          <div className={s.pieSkeleton}>
            <Skeleton variant="circle" width={200} height={200} />
          </div>
          <div className={s.list}>
            {Array.from({ length: SKELETON_ROWS }, (_, i) => (
              <div key={i} className={s.skeletonRow}>
                <Skeleton variant="circle" width={10} height={10} />
                <Skeleton variant="text" width="50%" height={14} />
                <Skeleton variant="text" width={56} height={14} className={s.skeletonSum} />
              </div>
            ))}
          </div>
        </div>
      )}
      {!error && !loading && categories.length === 0 && (
        <EmptyPage
          icon={<HiOutlineArrowsRightLeft size={48} />}
          title="Please add an expense to get started."
        />
      )}

      {!error && !loading && categories.length > 0 && (
        <div className={s.layout}>
          <div className={s.chart}>
            <PieChart
              items={items}
              periodLabel={periodLabel}
              onSliceClick={handleSliceClick}
              size="default"
              sliceRadius={6}
              sliceGap={1}
              slicePadAngle={0.2}
              sliceBorderColor="transparent"
              negative
            />
          </div>

          <div className={s.list}>
            {categories.map((category) => (
              <button
                key={category.slug}
                type="button"
                className={s.row}
                onClick={() => onSelectCategory(category.slug)}
              >
                <span className={s.dot} style={{ backgroundColor: category.color }} />
                <span className={s.title}>{category.title}</span>
                <span className={s.sum}>
                  {category.sum > 0 ? '-' : ''}
                  {formatCurrency(category.sum)}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );

  if (plain) {
    return <div className={s.plain}>{body}</div>;
  }

  return (
    <Card className={s.card}>
      <div className={s.head}>
        <BiPieChartAlt2 aria-hidden="true" />
        <h2 className="headline-small">{title}</h2>
      </div>
      {body}
    </Card>
  );
};

export default CategorySpending;
